import React, { useState, useEffect } from 'react';
import { XMarkIcon, PhotoIcon, TrashIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { cn } from '../utils';

const CarForm = ({ isOpen, onClose, car = null, onCarSaved }) => {
  const isEdit = !!car;
  
  const [formData, setFormData] = useState({
    numberPlate: '',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    vin: '',
    engineType: 'petrol',
    engineCapacity: '',
    transmission: 'manual',
    driveType: 'fwd',
    bodyType: 'sedan',
    fuelCapacity: '',
    seatingCapacity: 5,
    mileage: '',
    registrationDate: '',
    insuranceExpiryDate: '',
    roadTaxExpiryDate: '',
    motExpiryDate: '',
    owner: {
      name: '',
      phone: '',
      email: '',
      address: ''
    },
    serviceInterval: 6,
    notes: '',
    // German specific fields
    firma: '',
    fahrer: '',
    schluesselnummer: '',
    fahrgestellnummer: '',
    reifengroesse: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  // Engine type options
  const engineTypes = [
    { value: 'petrol', label: 'Petrol' },
    { value: 'diesel', label: 'Diesel' },
    { value: 'electric', label: 'Electric' },
    { value: 'hybrid', label: 'Hybrid' },
    { value: 'lpg', label: 'LPG' },
    { value: 'cng', label: 'CNG' },
    { value: 'other', label: 'Other' }
  ];

  const transmissionTypes = [
    { value: 'manual', label: 'Manual' },
    { value: 'automatic', label: 'Automatic' },
    { value: 'cvt', label: 'CVT' },
    { value: 'semi-automatic', label: 'Semi-Automatic' }
  ];

  const driveTypes = [
    { value: 'fwd', label: 'Front-Wheel Drive (FWD)' },
    { value: 'rwd', label: 'Rear-Wheel Drive (RWD)' },
    { value: 'awd', label: 'All-Wheel Drive (AWD)' },
    { value: '4wd', label: '4-Wheel Drive (4WD)' }
  ];

  const bodyTypes = [
    { value: 'sedan', label: 'Sedan' },
    { value: 'hatchback', label: 'Hatchback' },
    { value: 'suv', label: 'SUV' },
    { value: 'coupe', label: 'Coupe' },
    { value: 'convertible', label: 'Convertible' },
    { value: 'wagon', label: 'Wagon' },
    { value: 'pickup', label: 'Pickup' },
    { value: 'van', label: 'Van' },
    { value: 'truck', label: 'Truck' },
    { value: 'motorcycle', label: 'Motorcycle' },
    { value: 'other', label: 'Other' }
  ];

  // Populate form when editing
  useEffect(() => {
    if (isOpen && car) {
      setFormData({
        ...car,
        registrationDate: car.registrationDate ? new Date(car.registrationDate).toISOString().split('T')[0] : '',
        insuranceExpiryDate: car.insuranceExpiryDate ? new Date(car.insuranceExpiryDate).toISOString().split('T')[0] : '',
        roadTaxExpiryDate: car.roadTaxExpiryDate ? new Date(car.roadTaxExpiryDate).toISOString().split('T')[0] : '',
        motExpiryDate: car.motExpiryDate ? new Date(car.motExpiryDate).toISOString().split('T')[0] : '',
        owner: car.owner || { name: '', phone: '', email: '', address: '' }
      });
      setExistingImages(car.images || []);
    } else if (isOpen) {
      resetForm();
    }
  }, [isOpen, car]);

  const resetForm = () => {
    setFormData({
      numberPlate: '',
      make: '',
      model: '',
      year: new Date().getFullYear(),
      color: '',
      vin: '',
      engineType: 'petrol',
      engineCapacity: '',
      transmission: 'manual',
      driveType: 'fwd',
      bodyType: 'sedan',
      fuelCapacity: '',
      seatingCapacity: 5,
      mileage: '',
      registrationDate: '',
      insuranceExpiryDate: '',
      roadTaxExpiryDate: '',
      motExpiryDate: '',
      owner: {
        name: '',
        phone: '',
        email: '',
        address: ''
      },
      serviceInterval: 6,
      notes: '',
      // German specific fields
      firma: '',
      fahrer: '',
      schluesselnummer: '',
      fahrgestellnummer: '',
      reifengroesse: ''
    });
    setErrors({});
    setSelectedImages([]);
    setImagePreviewUrls([]);
    setExistingImages([]);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    
    if (name.startsWith('owner.')) {
      const ownerField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        owner: {
          ...prev.owner,
          [ownerField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'number' ? parseInt(value) || '' : value
      }));
    }

    // Clear errors for the field being edited
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 10;
    const maxSize = 10 * 1024 * 1024; // 10MB per file

    if (files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} images allowed`);
      return;
    }

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 10MB`);
        return false;
      }
      return true;
    });

    setSelectedImages(validFiles);

    // Create preview URLs
    const previewUrls = validFiles.map(file => URL.createObjectURL(file));
    setImagePreviewUrls(previewUrls);
  };

  const removeNewImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviewUrls(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingImage = async (imageId) => {
    if (!isEdit) return;
    
    try {
      await apiService.deleteCarImage(car._id, imageId);
      setExistingImages(prev => prev.filter(img => img._id !== imageId));
      toast.success('Image deleted successfully');
    } catch (error) {
      toast.error('Failed to delete image');
      console.error('Delete image error:', error);
    }
  };

  const setPrimaryImage = async (imageId) => {
    if (!isEdit) return;
    
    try {
      await apiService.setPrimaryCarImage(car._id, imageId);
      setExistingImages(prev => prev.map(img => ({
        ...img,
        isPrimary: img._id === imageId
      })));
      toast.success('Primary image updated');
    } catch (error) {
      toast.error('Failed to set primary image');
      console.error('Set primary image error:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.numberPlate.trim()) newErrors.numberPlate = 'Number plate is required';
    if (!formData.make.trim()) newErrors.make = 'Make is required';
    if (!formData.model.trim()) newErrors.model = 'Model is required';
    if (!formData.year || formData.year < 1900) newErrors.year = 'Valid year is required';
    if (!formData.owner.name.trim()) newErrors['owner.name'] = 'Owner name is required';
    if (!formData.owner.phone.trim()) newErrors['owner.phone'] = 'Owner phone is required';
    // Note: Owner email is optional - no validation required

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix the errors below');
      return;
    }

    setLoading(true);
    try {
      const carData = {
        ...formData,
        numberPlate: formData.numberPlate.toUpperCase(),
        year: parseInt(formData.year),
        mileage: parseInt(formData.mileage) || 0,
        fuelCapacity: parseFloat(formData.fuelCapacity) || 0,
        seatingCapacity: parseInt(formData.seatingCapacity) || 5,
        serviceInterval: parseInt(formData.serviceInterval) || 6
      };

      let savedCar;
      if (isEdit) {
        const response = await apiService.updateCar(car._id, carData);
        savedCar = response.car;
        toast.success('Car updated successfully');
      } else {
        const response = await apiService.createCar(carData);
        savedCar = response.car;
        toast.success('Car created successfully');
      }

      // Upload new images if any selected
      if (selectedImages.length > 0) {
        try {
          const formDataImages = new FormData();
          selectedImages.forEach(file => {
            formDataImages.append('files', file);
          });

          const uploadResponse = await apiService.uploadCarImages(savedCar._id || savedCar.id, formDataImages);
          if (uploadResponse.success) {
            toast.success(`${selectedImages.length} image(s) uploaded successfully`);
          }
        } catch (uploadError) {
          console.error('Image upload error:', uploadError);
          console.error('Car ID for upload:', savedCar._id || savedCar.id);
          console.error('FormData files:', selectedImages.map(f => f.name));
          toast.error(`Car saved but failed to upload images: ${uploadError.message}`);
        }
      }

      onCarSaved?.(savedCar);
      onClose();
    } catch (error) {
      console.error('Car save error:', error);
      toast.error(error.message || 'Failed to save car');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-dark-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-600">
          <h2 className="text-xl font-semibold text-white">
            {isEdit ? 'Edit Car' : 'Add New Car'}
          </h2>
          <button
            onClick={onClose}
            className="text-dark-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Number Plate *
                </label>
                <input
                  type="text"
                  name="numberPlate"
                  value={formData.numberPlate}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors.numberPlate && "border-red-500"
                  )}
                  placeholder="e.g., ABC123"
                />
                {errors.numberPlate && <p className="text-red-500 text-sm mt-1">{errors.numberPlate}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Make *</label>
                <input
                  type="text"
                  name="make"
                  value={formData.make}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors.make && "border-red-500"
                  )}
                  placeholder="e.g., Toyota"
                />
                {errors.make && <p className="text-red-500 text-sm mt-1">{errors.make}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Model *</label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors.model && "border-red-500"
                  )}
                  placeholder="e.g., Camry"
                />
                {errors.model && <p className="text-red-500 text-sm mt-1">{errors.model}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Year *</label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  min="1900"
                  max={new Date().getFullYear() + 1}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors.year && "border-red-500"
                  )}
                />
                {errors.year && <p className="text-red-500 text-sm mt-1">{errors.year}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Color</label>
                <input
                  type="text"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., Silver"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">VIN</label>
                <input
                  type="text"
                  name="vin"
                  value={formData.vin}
                  onChange={handleInputChange}
                  maxLength="17"
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="17-character VIN"
                />
              </div>
            </div>
          </div>

          {/* German Specific Fields */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">German Vehicle Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Firma</label>
                <input
                  type="text"
                  name="firma"
                  value={formData.firma}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Fahrer</label>
                <input
                  type="text"
                  name="fahrer"
                  value={formData.fahrer}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Driver name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Schlüsselnummer</label>
                <input
                  type="text"
                  name="schluesselnummer"
                  value={formData.schluesselnummer}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Key number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Fahrgestellnummer</label>
                <input
                  type="text"
                  name="fahrgestellnummer"
                  value={formData.fahrgestellnummer}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Chassis number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Reifengröße</label>
                <input
                  type="text"
                  name="reifengroesse"
                  value={formData.reifengroesse}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 255/55R18"
                />
              </div>
            </div>
          </div>

          {/* Engine & Technical Details */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Engine & Technical Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Engine Type</label>
                <select
                  name="engineType"
                  value={formData.engineType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {engineTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Engine Capacity</label>
                <input
                  type="text"
                  name="engineCapacity"
                  value={formData.engineCapacity}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 2.0L, 1600cc"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Transmission</label>
                <select
                  name="transmission"
                  value={formData.transmission}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {transmissionTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Drive Type</label>
                <select
                  name="driveType"
                  value={formData.driveType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {driveTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Body Type</label>
                <select
                  name="bodyType"
                  value={formData.bodyType}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {bodyTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Fuel Capacity (L)</label>
                <input
                  type="number"
                  name="fuelCapacity"
                  value={formData.fuelCapacity}
                  onChange={handleInputChange}
                  min="0"
                  step="0.1"
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="e.g., 60"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Seating Capacity</label>
                <input
                  type="number"
                  name="seatingCapacity"
                  value={formData.seatingCapacity}
                  onChange={handleInputChange}
                  min="1"
                  max="50"
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Mileage</label>
                <input
                  type="number"
                  name="mileage"
                  value={formData.mileage}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Current mileage"
                />
              </div>
            </div>
          </div>

          {/* Important Dates */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Important Dates</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Registration Date</label>
                <input
                  type="date"
                  name="registrationDate"
                  value={formData.registrationDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Insurance Expiry</label>
                <input
                  type="date"
                  name="insuranceExpiryDate"
                  value={formData.insuranceExpiryDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Road Tax Expiry</label>
                <input
                  type="date"
                  name="roadTaxExpiryDate"
                  value={formData.roadTaxExpiryDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">MOT/Inspection Expiry</label>
                <input
                  type="date"
                  name="motExpiryDate"
                  value={formData.motExpiryDate}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Owner Information */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Owner Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-2">Owner Name *</label>
                <input
                  type="text"
                  name="owner.name"
                  value={formData.owner.name}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors['owner.name'] && "border-red-500"
                  )}
                  placeholder="Full name"
                />
                {errors['owner.name'] && <p className="text-red-500 text-sm mt-1">{errors['owner.name']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Phone Number *</label>
                <input
                  type="tel"
                  name="owner.phone"
                  value={formData.owner.phone}
                  onChange={handleInputChange}
                  className={cn(
                    "w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent",
                    errors['owner.phone'] && "border-red-500"
                  )}
                  placeholder="Phone number"
                />
                {errors['owner.phone'] && <p className="text-red-500 text-sm mt-1">{errors['owner.phone']}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Email</label>
                <input
                  type="email"
                  name="owner.email"
                  value={formData.owner.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Email address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-white mb-2">Service Interval (months)</label>
                <input
                  type="number"
                  name="serviceInterval"
                  value={formData.serviceInterval}
                  onChange={handleInputChange}
                  min="1"
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-white mb-2">Address</label>
                <textarea
                  name="owner.address"
                  value={formData.owner.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Full address"
                />
              </div>
            </div>
          </div>

          {/* Images */}
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Car Images</h3>
            
            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div className="mb-6">
                <h4 className="text-md font-medium text-white mb-3">Current Images</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {existingImages.map((image) => (
                    <div key={image._id} className="relative group">
                      <img
                        src={image.url}
                        alt={image.caption || 'Car image'}
                        className="w-full h-24 object-cover rounded-md"
                      />
                      <div className="absolute top-1 left-1">
                        <button
                          type="button"
                          onClick={() => setPrimaryImage(image._id)}
                          className={cn(
                            "p-1 rounded-full transition-colors",
                            image.isPrimary 
                              ? "bg-yellow-500 text-white" 
                              : "bg-black bg-opacity-50 text-white hover:bg-yellow-500"
                          )}
                        >
                          {image.isPrimary ? (
                            <StarIconSolid className="h-4 w-4" />
                          ) : (
                            <StarIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeExistingImage(image._id)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                      >
                        <TrashIcon className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New Image Upload */}
            <div>
              <div className="border-2 border-dashed border-dark-600 rounded-lg p-6 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                  id="car-image-upload"
                  disabled={loading}
                />
                <label
                  htmlFor="car-image-upload"
                  className="cursor-pointer flex flex-col items-center space-y-2"
                >
                  <PhotoIcon className="h-8 w-8 text-dark-400" />
                  <span className="text-sm text-dark-300">
                    Click to select images (up to 10 files, 10MB each)
                  </span>
                </label>
              </div>

              {/* New Image Previews */}
              {imagePreviewUrls.length > 0 && (
                <div className="mt-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    {imagePreviewUrls.map((url, index) => (
                      <div key={index} className="relative">
                        <img
                          src={url}
                          alt={`New preview ${index + 1}`}
                          className="w-full h-24 object-cover rounded-md"
                        />
                        <button
                          type="button"
                          onClick={() => removeNewImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                        <span className="text-xs text-dark-300 mt-1 block truncate">
                          {selectedImages[index]?.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">Additional Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 bg-dark-700 border border-dark-600 rounded-md text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Any additional notes about the car..."
            />
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end space-x-4 pt-6 border-t border-dark-600">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-dark-300 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
                </>
              ) : (
                <span>{isEdit ? 'Update Car' : 'Create Car'}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CarForm;